import { createApi } from '@reduxjs/toolkit/query/react';
import {
  ClassDetails,
  CourseDetails,
  CreateCourseRequest,
  CreateCourseResponse,
  GetClassesResponse,
  GetCoursesResponse,
} from '../../types/courseTypes';
import { baseQueryWithAuth } from './baseQuery';

export interface EnrollClassRequest {
  studentId: number;
  enrolledDate: string;
  timeSlot: string;
  tutorId: number;
}

export interface EnrollClassResponse {
  statusCode: number;
  message?: string;
  data?: any;
}

export interface TimeSlotItem {
  slot: string;
  isEnabled: boolean;
}

export interface GetTimeSlotsApiData {
  timeSlotArray: TimeSlotItem[];
  date: string;
}

export interface GetTimeSlotsApiResponse {
  statusCode: number;
  data: GetTimeSlotsApiData;
}

export const courseApi = createApi({
  reducerPath: 'courseApi',
  baseQuery: baseQueryWithAuth,
  endpoints: builder => ({
    getClasses: builder.query<ClassDetails[], void>({
      query: () => ({
        url: '/classes',
        method: 'GET',
        headers: {
          accept: '*/*',
        },
      }),
      transformResponse: (response: GetClassesResponse) => {
        return response.data;
      },
    }),
    createCourse: builder.mutation<CreateCourseResponse, CreateCourseRequest>({
      query: courseData => ({
        url: `/tutors/${courseData.tutorId}/courses`,
        method: 'POST',
        headers: {
          accept: '*/*',
          'Content-Type': 'application/json',
        },
        body: courseData,
      }),
    }),
    getCourses: builder.query<CourseDetails[], void>({
      query: () => ({
        url: '/courses',
        method: 'GET',
        headers: {
          accept: '*/*',
        },
      }),
      transformResponse: (response: GetCoursesResponse) => response.data.arrayObj,
    }),
    enrollCourse: builder.mutation<void, { courseId: number; schoolId: number }>({
      query: ({ courseId, schoolId }) => ({
        url: `/courses/${courseId}/enroll`,
        method: 'POST',
        headers: {
          accept: '*/*',
          'Content-Type': 'application/json',
        },
        body: {
          schoolId: schoolId,
        },
      }),
    }),
    enrollStudentInClass: builder.mutation<EnrollClassResponse, { classId: number; body: EnrollClassRequest }>({
      query: ({ classId, body }) => ({
        url: `/classes/${classId}/enroll`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: '*/*',
        },
        body: body,
      }),
    }),
    getTimeSlots: builder.query<GetTimeSlotsApiData, { classId: number; selectedDate: string }>({
      query: ({ classId, selectedDate }) => ({
        url: `/classes/${classId}/timeslots`,
        method: 'GET',
        params: { selectedDate },
        headers: {
          accept: '*/*',
        },
      }),
      transformResponse: (response: GetTimeSlotsApiResponse) => response.data,
      keepUnusedDataFor: 0, // Disable caching by setting cache time to 0
    }),
  }),
});

export const {
  useGetClassesQuery,
  useCreateCourseMutation,
  useGetCoursesQuery,
  useEnrollStudentInClassMutation,
  useGetTimeSlotsQuery,
} = courseApi;
